import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import useLearnProgress from "../../../hooks/useLearnProgress";
import { COUNTING_ENGINE_THEME, COUNTING_STAGE_ACCENT, countingBackdropAsset } from "../../../data/countingLadderCatalog";
import { countingWorldContainingLesson, countingWorldRoute, resolveCountingPlayerRoute } from "../../../utils/learnCountingProgress";
import LearnStage from "../shell/LearnStage";
import Celebration from "../feedback/Celebration";
import useLearnAudio from "../audio/useLearnAudio";
import { LEARN, usePrefersReducedMotion } from "../theme";
import { COUNTING_ENGINE_MAP } from "./games";

const CHEERS = ["You counted it!", "Spot on!", "Nice counting!", "That's right!", "Super!"];

function darken(hex, amount = 0.32) {
  const raw = String(hex || "#0065F8").replace("#", "");
  const n = parseInt(raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw, 16);
  const ch = (v) => Math.max(0, Math.round(v * (1 - amount))).toString(16).padStart(2, "0");
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

export default function CountingLessonPlayer() {
  const navigate = useNavigate();
  const { lessonNumber, activityId } = useParams();
  const { countingSet, loading, markCountingComplete } = useLearnProgress();
  const audio = useLearnAudio();
  const reduceMotion = usePrefersReducedMotion();

  const resolved = resolveCountingPlayerRoute({ lessonNumber, activityId, completed: countingSet });
  const lesson = resolved.lesson;
  const activity = resolved.activity;
  const index = lesson?.activities.findIndex((a) => a.id === activity?.id) ?? -1;
  const next = index >= 0 ? lesson.activities[index + 1] : null;
  const theme = activity ? COUNTING_ENGINE_THEME[activity.engine] : null;
  const accent = theme?.accent || (lesson && COUNTING_STAGE_ACCENT[lesson.stage]) || LEARN.course.counting.accent;
  const accentDeep = darken(accent);

  const [ready, setReady] = useState(false);
  const [pose, setPose] = useState("idle");
  const [playthrough, setPlaythrough] = useState(0);
  const [cheer] = useState(() => CHEERS[Math.floor(Math.random() * CHEERS.length)]);
  const wroteRef = useRef(false);

  useEffect(() => {
    wroteRef.current = false;
    setReady(false);
    setPose("idle");
    audio.cancel();
    if (!activity) return undefined;
    audio.prefetch([activity.instruction, next?.instruction].filter(Boolean));
    audio.sfx("whoosh", { volume: 0.5 });
    const t = setTimeout(() => audio.speak(activity.instruction, { interrupt: true }), 350);
    return () => { clearTimeout(t); audio.cancel(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.id, playthrough]);

  const goWorld = useCallback(() => {
    audio.cancel();
    const world = countingWorldContainingLesson(Number(lessonNumber));
    navigate(countingWorldRoute(world?.id || "meadow"));
  }, [audio, lessonNumber, navigate]);

  const handleComplete = useCallback(async () => {
    if (!activity || ready) return;
    setPose("cheer");
    audio.cancel();
    audio.sfx(next ? "sparkle" : "levelup");
    if (!countingSet.has(activity.id) && !wroteRef.current) {
      wroteRef.current = true;
      await markCountingComplete(activity.id);
    }
    setReady(true);
  }, [activity, audio, countingSet, markCountingComplete, next, ready]);

  const handleWrong = useCallback(() => {
    setPose("oops");
    audio.sfx("wrong", { volume: 0.6 });
  }, [audio]);

  const advance = useCallback(() => {
    setReady(false);
    if (next) {
      navigate(`/learn/counting/${lesson.number}/${next.id}`, { replace: true });
      return;
    }
    goWorld();
  }, [goWorld, lesson, navigate, next]);

  const replayCurrent = useCallback(() => {
    audio.cancel();
    setReady(false);
    setPlaythrough((n) => n + 1);
  }, [audio]);

  if (!loading && resolved.redirectTo) return <Navigate to={resolved.redirectTo} replace />;
  if (!lesson || !activity) return <LearnStage title="Counting" accent={accent} accentDeep={accentDeep} onClose={() => navigate("/learn/counting")} />;

  const Engine = COUNTING_ENGINE_MAP[activity.engine];

  return (
    <>
      <LearnStage
        title={activity.title}
        subtitle={`${lesson.title} · Game ${index + 1} of ${lesson.activities.length}`}
        prompt={activity.instruction}
        background={`counting/${countingBackdropAsset(lesson.number)}`}
        accent={accent}
        accentDeep={accentDeep}
        progress={{ done: index + (ready ? 1 : 0), total: lesson.activities.length }}
        companionPose={pose}
        hint={activity.skill}
        onClose={goWorld}
        onReplayPrompt={() => audio.replay(activity.instruction)}
        actionLabel={next ? "Next" : "Done"}
        actionEnabled={ready}
        onAction={advance}
        speaking={audio.speaking}
      >
        {Engine ? (
          <Engine
            key={`${activity.id}-${playthrough}`}
            activity={activity}
            speak={audio.speak}
            replay={audio.replay}
            sfx={audio.sfx}
            onComplete={handleComplete}
            onWrong={handleWrong}
            reduceMotion={reduceMotion}
          />
        ) : (
          <p style={{ textAlign: "center", color: "#fff", fontWeight: 700, textShadow: "0 2px 8px rgba(11,26,51,0.5)" }}>This activity is not available yet.</p>
        )}
      </LearnStage>

      <Celebration
        open={ready}
        kind={next ? "star" : "trophy"}
        title={next ? cheer : `${lesson.title} complete!`}
        detail={next ? `Up next: ${next.title}` : "The next stop on the map just opened."}
        cta={next ? "Next game" : "Back to the map"}
        onCta={advance}
        secondary="Play again"
        onSecondary={replayCurrent}
        reduceMotion={reduceMotion}
      />
    </>
  );
}
